import React, { useState, useEffect } from 'react';
import EditButtonRenderer from '../_components/EditButtonRenderer';

// CheckboxRenderer component with JSX
const CheckboxRenderer = ({ data, setShowResults, setSelectedRows }) => {
  const jobId = data.id;

  // Get initial state from localStorage
  const getSelectedIds = () => {
    try {
      return JSON.parse(localStorage.getItem('selectedJobIds') || '[]');
    } catch (error) {
      return [];
    }
  };

  // Local state for immediate UI response
  const [isChecked, setIsChecked] = useState(() =>
    getSelectedIds().includes(jobId)
  );

  // Sync with localStorage changes (in case other components update it)
  useEffect(() => {
    const selectedIds = getSelectedIds();
    setIsChecked(selectedIds.includes(jobId));
  }, [jobId]);

  const handleCheckboxChange = (event) => {
    const checked = event.target.checked;
    const currentSelectedIds = getSelectedIds();

    // Update local state immediately for instant UI feedback
    setIsChecked(checked);

    let updatedIds;
    if (checked) {
      // Add ID if not already present
      updatedIds = currentSelectedIds.includes(jobId)
        ? currentSelectedIds
        : [...currentSelectedIds, jobId];
      setShowResults(true);
    } else {
      // Remove ID from selection
      updatedIds = currentSelectedIds.filter((id) => id !== jobId);
    }

    // Update localStorage
    localStorage.setItem('selectedJobIds', JSON.stringify(updatedIds));

    // Update parent component state
    setSelectedRows(updatedIds);
  };

  return (
    <input
      type='checkbox'
      checked={isChecked}
      onChange={handleCheckboxChange}
    />
  );
};

export const columnDefs = ({
  setShowResults,
  setSelectedRows,
  setOpenEditModal,
  setSelectedRowForEdit,
}) => [
  {
    headerName: 'Show Results',
    field: 'id',
    resizable: false,
    width: 100,
    cellRenderer: CheckboxRenderer,
    cellRendererParams: {
      setShowResults,
      setSelectedRows,
    },
    tooltipValueGetter: (params) => `ID: ${params.data?.id}`,
  },
  {
    headerName: 'Request Id',
    field: 'id',
    resizable: false,
    width: 100,
    valueGetter: (params) => params.data?.id ?? 'N/A',
    tooltipValueGetter: (params) => `ID: ${params.data?.id ?? 'N/A'}`,
  },
  {
    headerName: 'Request Name',
    field: 'requestName',
    resizable: true,
    valueGetter: (params) => params.data?.requestName ?? 'N/A',
    tooltipValueGetter: (params) =>
      `Request Name: ${params.data?.requestName ?? 'N/A'}`,
  },
  {
    headerName: 'Keywords Ids',
    field: 'requestIds',
    hide: true, // Hidden by default
    resizable: true,
    valueGetter: (params) => params.data?.requestIds ?? 'N/A',
    tooltipValueGetter: (params) =>
      `Request ID: ${params.data?.requestIds ?? 'N/A'}`,
  },
  {
    headerName: 'Keyword',
    field: 'keywords',
    resizable: true,
    valueGetter: (params) => params.data?.keywords || 'N/A',
    tooltipValueGetter: (params) =>
      `Keywords: ${params.data?.keywords || 'N/A'}`,
  },
  {
    headerName: 'Platform',
    field: 'platform',
    resizable: false,
    width: 150,
    valueGetter: (params) => params.data?.platform || 'N/A',
    tooltipValueGetter: (params) =>
      `Platform: ${params.data?.platform || 'N/A'}`,
  },
  {
    headerName: 'Frequency',
    field: 'frequency',
    resizable: false,
    width: 120,
    valueGetter: (params) => params.data?.frequency || 'N/A',
    tooltipValueGetter: (params) =>
      `Frequency: ${params.data?.frequency || 'N/A'}`,
  },
  {
    headerName: 'Type',
    field: 'type',
    resizable: false,
    width: 120,
    valueGetter: (params) => params.data?.type || 'N/A',
    tooltipValueGetter: (params) => `Type: ${params.data?.type || 'N/A'}`,
  },
  {
    headerName: 'Last Run',
    field: 'updated_at',
    valueFormatter: (params) => {
      const value = params.value;
      const date = new Date(value);

      // Check if value is a valid date
      return isNaN(date.getTime()) ? value : date.toLocaleString();
    },
    tooltipValueGetter: (params) => {
      const value = params.value;
      const date = new Date(value);

      if (isNaN(date.getTime())) {
        return value ? `Status: ${value}` : 'No Last Run';
      }

      return `Last Run: ${date.toLocaleString()}`;
    },
  },
  {
    headerName: 'Actions',
    field: 'location',
    resizable: true,
    pinned: 'right',
    cellRenderer: EditButtonRenderer,
    cellRendererParams: {
      setOpenEditModal,
      setSelectedRowForEdit,
    },
    tooltipValueGetter: () => 'Edit this row',
  },
];
